import React, {type ChangeEventHandler, useCallback, useContext, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';


import {SwitcherLabel} from 'src/components/switcher-label';
import {MessageContext} from 'src/contexts/message-context';
import {ChangesInfo} from 'src/modules/profile/account/tabs/admin-account-api-v2/components/changes-info';
import {getOverageStatus} from 'src/modules/profile/account/tabs/admin-account-api-v2/views/controllers/limits/services/selectors';
import {
    getOverageEnabled,
    updateOverageStatus,
} from 'src/openapi/admin/apis/OverageSettingsControllerApi';
import {useAppDispatch} from 'src/store/hooks';

import {Group} from '@pb/kit-component-group';
import StateButton from '@pb/kit-component-state-button';
import {ConcurrentStrategies} from '@pb/middleware-api';

import type {AppState} from 'src/store/typedef';

type Props = {
    accountId: number,
    disabled?: boolean,
};

export const OverageController = (props: Props) => {
    const {accountId, disabled} = props;
    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();

    const {setErrorMessage} = useContext(MessageContext);
    const overageStatus = useSelector((state: AppState) => getOverageStatus(state, {accountId}));

    useEffect(() => {
        apiDispatch(getOverageEnabled({accountId}, {concurrentStrategy: ConcurrentStrategies.LAST})).then(response => {
            if (response.payload.error) {
                setErrorMessage(response.payload.data.message || 'Service is temporarily unavailable');
            }
        });
    }, [accountId, dispatch, apiDispatch, setErrorMessage]);

    const onUpdateOverage = useCallback<ChangeEventHandler<HTMLInputElement>>(event => {
        const enabled = event.currentTarget.checked;
        apiDispatch(updateOverageStatus({accountId, overageSettings: {enabled}}));
    }, [accountId, apiDispatch]);

    if (!overageStatus) {
        return null;
    }

    return (
        <Group direction="v" gap="l">
            <SwitcherLabel caption="Overage Charges">
                <StateButton
                    {...{
                        name: 'overage.charges',
                        checked: overageStatus.enabled,
                        onChange: onUpdateOverage,
                        disabled,
                    }}
                />
            </SwitcherLabel>
            <ChangesInfo data={overageStatus.changesInfo}/>
        </Group>
    );
};
